import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BACKEND_BASE_URL } from "../../config/app.config";
import { StateContext } from "../../store/context";
import "./AnnualReportQcQueue.css";

const resolveBaseUrl = () => {
  const envUrl =
    process.env.REACT_APP_ANNUAL_REPORT_API ??
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_BASE_URL ??
    "";
  if (typeof window !== "undefined") {
    const origin = window.location.origin || "";
    const isHttps = window.location.protocol === "https:";
    const candidate = (envUrl || BACKEND_BASE_URL || "").replace(/\/+$/, "");

    if (isHttps && candidate.startsWith("http://")) {
      if (origin && !origin.includes("localhost")) {
        return origin;
      }
      return candidate.replace(/^http:\/\//, "https://");
    }

    if (candidate) return candidate;
    if (origin) return origin;
  }
  return (envUrl || BACKEND_BASE_URL || "http://localhost:8000").replace(/\/+$/, "");
};

const API_BASE_URL = resolveBaseUrl();
const QC_STATEMENT_DISPLAY_ORDER = [
  "Profit or Loss",
  "Financial Position",
  "Cash Flows",
];
const QC_STATEMENT_LABELS: Record<string, string> = {
  "Profit or Loss": "Profit or Loss",
  "Financial Position": "Balance Sheet",
  "Cash Flows": "Cash Flow",
};
const ALLOWED_QC_STATEMENTS = new Set(QC_STATEMENT_DISPLAY_ORDER);

const toTrimmed = (value: any) => String(value || "").trim();
const normaliseStatementKey = (value: any) => {
  const raw = toTrimmed(value);
  const compact = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!compact) return "";
  if (compact.includes("balancesheet") || compact.includes("financialposition")) {
    return "Financial Position";
  }
  if (compact.includes("cashflow")) {
    return "Cash Flows";
  }
  if (compact.includes("changesinequity")) {
    return "Changes in Equity";
  }
  if (compact.includes("comprehensiveincome")) {
    return "Comprehensive Income";
  }
  if (compact.includes("profitorloss") || compact.includes("incomestatement")) {
    return "Profit or Loss";
  }
  return raw;
};
const getStatementSortRank = (statementName: string) => {
  const index = QC_STATEMENT_DISPLAY_ORDER.indexOf(statementName);
  return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
};
const getStatementLabel = (statementName: string) =>
  QC_STATEMENT_LABELS[statementName] || statementName || "Statement";
const getImageDataUrl = (image: any) => {
  if (!image) return "";
  if (typeof image === "string") {
    const trimmed = toTrimmed(image);
    if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    if (/^[a-z0-9+/=\s]+$/i.test(trimmed)) return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    return trimmed;
  }
  const directValue = toTrimmed(image?.dataUrl || image?.src || image?.url || image?.data_url || "");
  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(directValue)) return directValue;
  if (directValue.startsWith("/") || directValue.startsWith("http://") || directValue.startsWith("https://")) return directValue;
  const rawBase64 = toTrimmed(image?.base64 || image?.image_base64 || image?.rawBase64 || image?.raw_base64 || "");
  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(rawBase64)) return rawBase64;
  if (rawBase64 && /^[a-z0-9+/=\s]+$/i.test(rawBase64)) {
    const mimeType = toTrimmed(image?.mimeType || image?.mime_type) || "image/png";
    return `data:${mimeType};base64,${rawBase64.replace(/\s+/g, "")}`;
  }
  return directValue || rawBase64 || "";
};
const normaliseMetricKey = (value: any) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const isTrue = (value: any) =>
  value === true || String(value || '').trim().toLowerCase() === 'true';
const CASH_FLOW_METRIC_KEYS = new Set([
  "cashfromoperatingactivities",
  "cashfrominvestingactivities",
  "cashfromfinancingactivities",
  "capitalexpenditure",
  "netborrowings",
  "depreciation",
  "amortization",
  "amortisation",
  "cfoqtrly",
  "cfiqtrly",
  "cffqtrly",
  "capexpqtrly",
  "netborrowingsqtrly",
  "netborrowingsqrtly",
  "depqtrly",
  "amorqtrly",
]);

const PROFIT_LOSS_Q4_METRIC_KEYS = new Set([
  "revenue",
  "revenues",
  "grossprofit",
  "grossprofits",
  "operatingprofit",
  "operatingprofits",
  "interestexpense",
  "interestincome",
  "pbt",
  "profitbeforetax",
  "profitbeforetaxation",
  "profitbeforetaxes",
  "pretaxprofit",
  "pretaxprofits",
  "pretaxincome",
  "taxation",
  "tax",
  "taxes",
  "incometax",
  "taxexpense",
  "taxexpenses",
  "netprofit",
  "netprofits",
  "epsactuals",
  "eps",
]);

const REQUIRED_SOP_METRIC_KEYS = new Set([
  "revenue",
  "revenues",
  "grossprofit",
  "grossprofits",
  "operatingprofit",
  "operatingprofits",
  "interestexpense",
  "interestincome",
  "profitbeforetax",
  "pbt",
  "profitbeforetaxation",
  "profitbeforetaxes",
  "pretaxprofit",
  "pretaxprofits",
  "pretaxincome",
  "taxation",
  "tax",
  "taxes",
  "incometax",
  "taxexpense",
  "taxexpenses",
  "netprofit",
  "netprofits",
  "eps",
  "epsactuals",
  "shareprice",
  "totalnumberofissuedshares",
  "totalnumberofshares",
  "fixedassets",
  "totalcurrentassets",
  "totalassets",
  "inventories",
  "tradereceivables",
  "cash",
  "totalequity",
  "tradepayables",
  "totalcurrentliabilities",
  "totalliabilities",
  "totaldebt",
  "bookvalue",
  "depreciation",
  "amortization",
  "amortisation",
  "cashfromoperatingactivities",
  "cfo",
  "capitalexpenditure",
  "capex",
  "cashfrominvestingactivities",
  "cfi",
  "netborrowings",
  "cashfromfinancingactivities",
  "cff",
]);

const toAuthorizationHeader = (token: any) => {
  const trimmed = String(token || "").trim();
  if (!trimmed) {
    return "";
  }
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

const cloneJsonValue = (value: any) => {
  if (value === null || typeof value === "undefined") {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_error) {
    return value;
  }
};

const parseNumericValue = (value: any): number | null => {
  if (value === null || typeof value === "undefined") {
    return null;
  }
  let raw = String(value).trim();
  if (!raw || raw === "-") {
    return null;
  }
  const hasPercent = /%|percent|pct/i.test(raw);
  if (hasPercent) {
    return null;
  }
  let negative = false;
  if (/^\(.*\)$/.test(raw)) {
    negative = true;
    raw = raw.slice(1, -1);
  }
  if (raw.endsWith("-")) {
    negative = true;
    raw = raw.slice(0, -1);
  }
  raw = raw
    .replace(/[\u2212\u2012\u2013\u2014]/g, "-")
    .replace(/[, ]+/g, "")
    .replace(/[^0-9.\-]/g, "");
  if (!raw || raw === "-" || raw === "." || raw === "-.") {
    return null;
  }
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return negative ? -Math.abs(numeric) : numeric;
};

const normalizeCurrencyConversionConfig = (...sources: any[]) => {
  const raw = sources.find((source) => source && typeof source === "object");
  if (!raw) {
    return null;
  }

  const baseCurrency = String(
    raw.baseCurrency ||
    raw.base_currency ||
    raw.sourceCurrency ||
    raw.source_currency ||
    ""
  ).trim().toUpperCase();
  const targetCurrency = String(
    raw.targetCurrency ||
    raw.target_currency ||
    raw.toCurrency ||
    raw.to_currency ||
    ""
  ).trim().toUpperCase();
  const hasExplicitEnabled =
    typeof raw.enabled !== "undefined" ||
    typeof raw.enable_currency_conversion !== "undefined";
  const enabled = hasExplicitEnabled
    ? Boolean(raw.enabled || raw.enable_currency_conversion)
    : Boolean(baseCurrency && targetCurrency && baseCurrency !== targetCurrency);

  if (!baseCurrency && !targetCurrency) {
    return null;
  }

  return {
    enabled,
    baseCurrency: baseCurrency || "USD",
    targetCurrency: targetCurrency || "AED",
    base_currency: baseCurrency || "USD",
    target_currency: targetCurrency || "AED",
  };
};

const buildEditableQcPayload = (payload: any) => {
  const nextPayload = cloneJsonValue(payload) || {};
  const reviewSnapshot =
    nextPayload?.reviewSnapshot && typeof nextPayload.reviewSnapshot === "object"
      ? nextPayload.reviewSnapshot
      : {};
  const reviewStatements = Array.isArray(reviewSnapshot?.statements)
    ? reviewSnapshot.statements
    : [];

  const convConfig = normalizeCurrencyConversionConfig(
    nextPayload?.currencyConversion,
    nextPayload?.currency_conversion,
    nextPayload?.sopMetadata?.currencyConversion,
    nextPayload?.sopMetadata?.currency_conversion,
    nextPayload?.sopMetadata?.metadata?.currencyConversion,
    nextPayload?.sopMetadata?.metadata?.currency_conversion,
    reviewSnapshot?.currencyConversion,
    reviewSnapshot?.currency_conversion,
    reviewSnapshot?.metadata?.currencyConversion,
    reviewSnapshot?.metadata?.currency_conversion
  );

  return {
    pdfName: nextPayload?.pdfName || reviewSnapshot?.metadata?.pdfName || "",
    sopSummary: Array.isArray(nextPayload?.sopSummary)
      ? nextPayload.sopSummary
      : Array.isArray(reviewSnapshot?.sopSummary)
        ? reviewSnapshot.sopSummary
        : [],
    sopMetadata: {
      ...(nextPayload?.sopMetadata && typeof nextPayload.sopMetadata === "object"
        ? nextPayload.sopMetadata
        : {}),
      ...(convConfig ? { currencyConversion: convConfig } : {}),
    },
    currencyConversion: convConfig,
    reviewSnapshot: {
      ...reviewSnapshot,
      statements: reviewStatements,
      ...(convConfig ? { currencyConversion: convConfig } : {}),
    },
    reviewedStatements:
      nextPayload?.reviewedStatements && typeof nextPayload.reviewedStatements === "object"
        ? nextPayload.reviewedStatements
        : reviewSnapshot?.metadata?.reviewedStatements &&
            typeof reviewSnapshot.metadata.reviewedStatements === "object"
          ? reviewSnapshot.metadata.reviewedStatements
          : {},
    manualSopAdjustments:
      nextPayload?.manualSopAdjustments && typeof nextPayload.manualSopAdjustments === "object"
        ? nextPayload.manualSopAdjustments
        : {},
  };
};

const AnnualReportQcQueue: React.FC = () => {
  const { authUser } = useContext(StateContext);
  const [rows, setRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [selectedVersionPayload, setSelectedVersionPayload] = useState<any | null>(null);
  const [editablePayload, setEditablePayload] = useState<any | null>(null);
  const [selectedVersionLoading, setSelectedVersionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [message, setMessage] = useState("");
  const [queueTab, setQueueTab] = useState<"PENDING" | "APPROVED" | "REJECTED">("PENDING");
  const [imageZoom, setImageZoom] = useState(1.35);
  const [reviewEditorFullscreen, setReviewEditorFullscreen] = useState(false);
  const [activeReviewStatement, setActiveReviewStatement] = useState("");
  const [reviewTab, setReviewTab] = useState<"editor" | "sop">("sop");
  const [sideImageVisible, setSideImageVisible] = useState(true);
  const [showOnlyRequiredMetrics, setShowOnlyRequiredMetrics] = useState(true);
  const [tickerFilter, setTickerFilter] = useState("");
  const [submitterFilter, setSubmitterFilter] = useState("");
  const [conversionEnabled, setConversionEnabled] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [targetCurrency, setTargetCurrency] = useState("LKR");
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateDate, setExchangeRateDate] = useState("");

  const authHeaders = useMemo(() => {
    const headers: any = {};
    const authToken = localStorage.getItem("token");
    const authorization = toAuthorizationHeader(authToken);
    if (authorization) {
      headers.Authorization = authorization;
    }
    if (authUser?.role_id) {
      headers.role_id = String(authUser.role_id);
      headers["x-role-id"] = String(authUser.role_id);
    }
    const userId = authUser?.id || authUser?.userId;
    if (userId) {
      headers.user_id = String(userId);
      headers["x-user-id"] = String(userId);
    }
    if (authUser?.role_name) {
      headers.role_name = String(authUser.role_name);
      headers["x-role-name"] = String(authUser.role_name);
    }
    if (authUser?.full_name || authUser?.name) {
      headers["x-full-name"] = String(authUser.full_name || authUser.name);
    }
    if (Array.isArray(authUser?.allowed)) {
      headers["x-allowed-keys"] = authUser.allowed.join(",");
    }
    if (authUser?.email) {
      headers["x-user-email"] = String(authUser.email);
    }
    if (authUser?.username) {
      headers["x-username"] = String(authUser.username);
    }
    return headers;
  }, [
    authUser?.allowed,
    authUser?.full_name,
    authUser?.id,
    authUser?.name,
    authUser?.role_id,
    authUser?.role_name,
    authUser?.userId,
    authUser?.email,
    authUser?.username,
  ]);

  useEffect(() => {
    const convConfig = normalizeCurrencyConversionConfig(
      editablePayload?.currencyConversion,
      editablePayload?.currency_conversion,
      editablePayload?.sopMetadata?.currencyConversion,
      editablePayload?.sopMetadata?.currency_conversion,
      editablePayload?.sopMetadata?.metadata?.currencyConversion,
      editablePayload?.sopMetadata?.metadata?.currency_conversion,
      editablePayload?.reviewSnapshot?.currencyConversion,
      editablePayload?.reviewSnapshot?.currency_conversion,
      editablePayload?.reviewSnapshot?.metadata?.currencyConversion,
      editablePayload?.reviewSnapshot?.metadata?.currency_conversion,
      selectedVersionPayload?.payload?.currencyConversion,
      selectedVersionPayload?.payload?.currency_conversion,
      selectedVersionPayload?.payload?.sopMetadata?.currencyConversion,
      selectedVersionPayload?.payload?.sopMetadata?.currency_conversion,
      selectedVersionPayload?.payload?.sopMetadata?.metadata?.currencyConversion,
      selectedVersionPayload?.payload?.sopMetadata?.metadata?.currency_conversion,
      selectedVersionPayload?.payload?.reviewSnapshot?.currencyConversion,
      selectedVersionPayload?.payload?.reviewSnapshot?.currency_conversion,
      selectedVersionPayload?.payload?.reviewSnapshot?.metadata?.currencyConversion,
      selectedVersionPayload?.payload?.reviewSnapshot?.metadata?.currency_conversion
    );

    if (convConfig) {
      setConversionEnabled(convConfig.enabled);
      setBaseCurrency(convConfig.baseCurrency);
      setTargetCurrency(convConfig.targetCurrency);
    }
  }, [editablePayload, selectedVersionPayload]);

  useEffect(() => {
    if (!conversionEnabled || !baseCurrency || !targetCurrency || baseCurrency === targetCurrency) {
      setExchangeRate(1);
      return;
    }
    let isMounted = true;
    setExchangeRateLoading(true);
    const quarterDateStr = String(
      selected?.quarter_date ||
      selectedVersionPayload?.workflow?.quarter_date ||
      editablePayload?.selectedQuarter?.date ||
      selectedVersionPayload?.version?.quarter_date ||
      ""
    ).trim() || new Date().toISOString().slice(0, 10);

    axios
      .get(`${API_BASE_URL}/api/annual-report/exchange-rate`, {
        headers: authHeaders,
        params: {
          baseCurrency,
          targetCurrency,
          date: quarterDateStr,
        },
      })
      .then((res) => {
        if (isMounted) {
          const rateVal = Number(res?.data?.rate);
          setExchangeRate(Number.isFinite(rateVal) && rateVal > 0 ? rateVal : null);
          setExchangeRateDate(res?.data?.date || quarterDateStr);
        }
      })
      .catch((err) => {
        console.warn("[QC Queue] Exchange rate fetch failed:", err);
        if (isMounted) {
          setExchangeRate(null);
          setExchangeRateDate(quarterDateStr);
        }
      })
      .finally(() => {
        if (isMounted) setExchangeRateLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [
    authHeaders,
    conversionEnabled,
    baseCurrency,
    targetCurrency,
    selected?.quarter_date,
    selectedVersionPayload?.workflow?.quarter_date,
    selectedVersionPayload?.version?.quarter_date,
    editablePayload?.selectedQuarter?.date,
  ]);

  const updateCurrencyConversionConfig = (enabled: boolean, baseCur: string, targetCur: string) => {
    setConversionEnabled(enabled);
    setBaseCurrency(baseCur);
    setTargetCurrency(targetCur);
    setEditablePayload((current: any) => {
      if (!current) return current;
      const convObj = {
        enabled,
        baseCurrency: baseCur,
        targetCurrency: targetCur,
        base_currency: baseCur,
        target_currency: targetCur,
      };
      return {
        ...current,
        currencyConversion: convObj,
        sopMetadata: {
          ...(current.sopMetadata || {}),
          currencyConversion: convObj,
          currency_conversion: convObj,
        },
        reviewSnapshot: {
          ...(current.reviewSnapshot || {}),
          currencyConversion: convObj,
          currency_conversion: convObj,
        },
      };
    });
  };

  const formatCurrencyPreviewValue = (value: number, fractionDigits = 4) =>
    value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    });

  const getEffectiveConversionValue = (itemOrValue: any) => {
    if (typeof itemOrValue !== "object" || itemOrValue === null) {
      return { rawValue: itemOrValue, sourceLabel: "Actual value" };
    }

    if (
      itemOrValue.profitLossQuarterAdjustment &&
      applyAdjustment &&
      itemOrValue.profitLossQuarterAdjustment?.quarterValue !== undefined &&
      itemOrValue.profitLossQuarterAdjustment?.quarterValue !== null
    ) {
      return {
        rawValue: itemOrValue.profitLossQuarterAdjustment.quarterValue,
        sourceLabel: "Actual saved value",
      };
    }

    if (
      itemOrValue.cashFlowQuarterAdjustment &&
      itemOrValue.cashFlowQuarterAdjustment?.quarterValue !== undefined &&
      itemOrValue.cashFlowQuarterAdjustment?.quarterValue !== null
    ) {
      return {
        rawValue: itemOrValue.cashFlowQuarterAdjustment.quarterValue,
        sourceLabel: "Actual saved value",
      };
    }

    return { rawValue: itemOrValue.value, sourceLabel: "Actual value" };
  };

  const renderConversionPreview = (item: any) => {
    if (!conversionEnabled || baseCurrency === targetCurrency) {
      return null;
    }

    const metricKey = normaliseMetricKey(item?.metric);
    if (
      metricKey.includes("shareprice") ||
      metricKey.includes("issuedshares") ||
      metricKey.includes("numberofshares") ||
      metricKey === "sharesoutstanding" ||
      metricKey === "totnoofshares"
    ) {
      return null;
    }

    const { rawValue } = getEffectiveConversionValue(item);
    const actualValue = parseNumericValue(rawValue);
    if (actualValue === null || !Number.isFinite(actualValue)) {
      return null;
    }

    if (exchangeRateLoading) {
      return <div className="sop-conversion-preview muted">Fetching rate...</div>;
    }

    const safeRate = Number(exchangeRate);
    if (!Number.isFinite(safeRate) || safeRate <= 0) {
      return <div className="sop-conversion-preview warning">Rate unavailable</div>;
    }

    const convertedValue = actualValue * safeRate;
    return (
      <div className="sop-conversion-preview">
        <span>{targetCurrency} {formatCurrencyPreviewValue(convertedValue)}</span>
        <small>1 {baseCurrency} = {formatCurrencyPreviewValue(safeRate, 6)} {targetCurrency}</small>
      </div>
    );
  };

  const renderSopValueControl = (item: any) => {
    const conversionPreview = renderConversionPreview(item);
    const input = (
      <input
        type="text"
        className="sop-row-input"
        aria-label={`${item.metric} original value${conversionPreview ? ` (${baseCurrency})` : ""}`}
        value={item.value ?? ""}
        onChange={(e) => handleQcSopValueChange(item.originalIndex, e.target.value)}
      />
    );

    if (!conversionPreview) {
      return input;
    }

    return (
      <div className="sop-value-stack">
        {conversionPreview}
        <label className="sop-original-value">
          <span>Original ({baseCurrency})</span>
          {input}
        </label>
      </div>
    );
  };

  useEffect(() => {
    setShowOnlyRequiredMetrics(true);
  }, [queueTab, selected]);

  useEffect(() => {
    setTickerFilter("");
    setSubmitterFilter("");
  }, [queueTab]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      let queueRows: any[] = [];

      if (queueTab === "PENDING") {
        const response = await axios.get(
          `${API_BASE_URL}/api/annual-report/workflows/pending`,
          {
            headers: authHeaders,
            params: {
              limit: 200,
            },
          }
        );
        queueRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      } else {
        const historyResponse = await axios.get(
          `${API_BASE_URL}/api/annual-report/workflows/history`,
          {
            headers: authHeaders,
            params: {
              limit: 120,
              status: queueTab === "APPROVED" ? "COMMITTED" : "REJECTED",
              include_details: false,
            },
          }
        );
        const historyRows = Array.isArray(historyResponse?.data?.rows)
          ? historyResponse.data.rows
          : [];
        queueRows = historyRows;
      }

      setRows(queueRows);
      if (!queueRows.length) {
        setSelected(null);
        setSelectedVersionPayload(null);
        setEditablePayload(null);
      }
    } catch (error: any) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.details;
      setRows([]);
      setSelected(null);
      setSelectedVersionPayload(null);
      setEditablePayload(null);
      setMessage(serverMessage || "Failed to load QC queue.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, queueTab]);

  const loadVersionPayload = useCallback(
    async (workflowId: number, versionId?: number | null) => {
      if (!workflowId) return;
      setSelectedVersionLoading(true);
      setMessage("");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/annual-report/workflows/version`,
          {
            headers: authHeaders,
            params: {
              workflow_id: workflowId,
              version_id: versionId || undefined,
            },
          }
        );
        setSelectedVersionPayload(response?.data || null);
        setEditablePayload(buildEditableQcPayload(response?.data?.payload || null));
      } catch (error: any) {
        const serverMessage = error?.response?.data?.error || error?.response?.data?.details;
        setSelectedVersionPayload(null);
        setEditablePayload(null);
        setMessage(serverMessage || "Failed to load workflow details for review.");
      } finally {
        setSelectedVersionLoading(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleSelect = async (row: any) => {
    setSelected(row);
    setSelectedVersionPayload(null);
    setEditablePayload(null);
    setMessage("");
    setReviewTab("sop");
    const workflowId = Number(row?.workflow_id || row?.id || 0);
    const currentVersion = Array.isArray(row?.versions)
      ? row.versions.find((item: any) => Number(item?.is_current || 0) === 1) || row.versions[0]
      : null;
    const selectedVersionId = Number(
      row?.version_id || row?.current_version_id || currentVersion?.id || 0
    ) || null;
    await loadVersionPayload(workflowId, selectedVersionId);
  };

  useEffect(() => {
    const statementNames = Array.isArray(editablePayload?.reviewSnapshot?.statements)
      ? editablePayload.reviewSnapshot.statements
          .map((statement: any) => normaliseStatementKey(statement?.statement))
          .filter(Boolean)
      : [];
    if (!statementNames.length) {
      setActiveReviewStatement("");
      return;
    }
    setActiveReviewStatement((current) => (
      statementNames.includes(current) ? current : statementNames[0]
    ));
  }, [editablePayload]);

  const handleApprove = async () => {
    if (!selected?.workflow_id && !selected?.id) {
      return;
    }
    setActionLoading(true);
    setMessage("");
    try {
      const workflowId = Number(selected?.workflow_id || selected?.id || 0);
      const versionId = Number(
        selected?.version_id ||
        selected?.current_version_id ||
        (Array.isArray(selected?.versions)
          ? (selected.versions.find((item: any) => Number(item?.is_current || 0) === 1) || selected.versions[0])?.id
          : 0) ||
        0
      ) || null;
      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/approve`,
        {
          workflow_id: workflowId,
          version_id: versionId,
          comment: comment || null,
          payload_override: editablePayload || undefined,
        },
        { headers: authHeaders }
      );
      const savedCount = Number(response?.data?.counts?.saved || 0);
      setMessage(`Approved and committed successfully. Saved ${savedCount} metrics.`);
      setComment("");
      await loadQueue();
      setSelected(null);
      setSelectedVersionPayload(null);
      setEditablePayload(null);
    } catch (error: any) {
      const serverError = error?.response?.data?.error;
      const serverDetails = error?.response?.data?.details;
      const serverMessage = serverError && serverDetails
        ? `${serverError} ${serverDetails}`
        : serverError || serverDetails;
      setMessage(serverMessage || "Failed to approve workflow.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected?.workflow_id && !selected?.id) {
      return;
    }
    setActionLoading(true);
    setMessage("");
    try {
      const workflowId = Number(selected?.workflow_id || selected?.id || 0);
      const versionId = Number(
        selected?.version_id ||
        selected?.current_version_id ||
        (Array.isArray(selected?.versions)
          ? (selected.versions.find((item: any) => Number(item?.is_current || 0) === 1) || selected.versions[0])?.id
          : 0) ||
        0
      ) || null;
      await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/reject`,
        {
          workflow_id: workflowId,
          version_id: versionId,
          comment: comment || null,
        },
        { headers: authHeaders }
      );
      setMessage("Workflow rejected successfully.");
      setComment("");
      await loadQueue();
      setSelected(null);
      setSelectedVersionPayload(null);
      setEditablePayload(null);
    } catch (error: any) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.details;
      setMessage(serverMessage || "Failed to reject workflow.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleQcStatementValueChange = useCallback((
    statementName: string,
    lineIndex: number,
    columnName: string,
    value: string
  ) => {
    setEditablePayload((current: any) => {
      if (!current?.reviewSnapshot || !Array.isArray(current.reviewSnapshot.statements)) {
        return current;
      }
      const nextStatements = current.reviewSnapshot.statements.map((statement: any) => {
        if (normaliseStatementKey(statement?.statement) !== normaliseStatementKey(statementName)) {
          return statement;
        }
        const nextLineItems = Array.isArray(statement?.lineItems)
          ? statement.lineItems.map((lineItem: any, index: number) => {
              if (index !== lineIndex) {
                return lineItem;
              }
              return {
                ...lineItem,
                values: {
                  ...(lineItem?.values || {}),
                  [columnName]: value,
                },
              };
            })
          : [];
        return {
          ...statement,
          lineItems: nextLineItems,
        };
      });
      return {
        ...current,
        reviewSnapshot: {
          ...(current.reviewSnapshot || {}),
          statements: nextStatements,
        },
      };
    });
  }, []);

  const handleQcSopValueChange = useCallback((index: number, value: string) => {
    setEditablePayload((current: any) => {
      if (!Array.isArray(current?.sopSummary)) {
        return current;
      }
      const nextSopSummary = current.sopSummary.map((entry: any, entryIndex: number) => (
        entryIndex === index
          ? {
              ...entry,
              value,
              manual: true,
              cashFlowQuarterAdjustment: null,
              profitLossQuarterAdjustment: null,
            }
          : entry
      ));
      const nextReviewSnapshot =
        current?.reviewSnapshot && typeof current.reviewSnapshot === "object"
          ? {
              ...current.reviewSnapshot,
              sopSummary: nextSopSummary,
            }
          : current?.reviewSnapshot;
      return {
        ...current,
        sopSummary: nextSopSummary,
        reviewSnapshot: nextReviewSnapshot,
      };
    });
  }, []);

  const applyAdjustment = useMemo(() => {
    return Boolean(
      isTrue(editablePayload?.sopMetadata?.applyProfitLossQ4CumulativeAdjustment) ||
      isTrue(editablePayload?.reviewSnapshot?.metadata?.applyProfitLossQ4CumulativeAdjustment) ||
      isTrue(selectedVersionPayload?.payload?.sopMetadata?.applyProfitLossQ4CumulativeAdjustment) ||
      isTrue(selectedVersionPayload?.payload?.reviewSnapshot?.metadata?.applyProfitLossQ4CumulativeAdjustment)
    );
  }, [
    editablePayload?.sopMetadata,
    editablePayload?.reviewSnapshot?.metadata,
    selectedVersionPayload?.payload?.sopMetadata,
    selectedVersionPayload?.payload?.reviewSnapshot?.metadata,
  ]);

  const sopSummary = useMemo(() => {
    const raw = Array.isArray(editablePayload?.sopSummary)
      ? editablePayload.sopSummary
      : Array.isArray(editablePayload?.reviewSnapshot?.sopSummary)
        ? editablePayload.reviewSnapshot.sopSummary
        : Array.isArray(selectedVersionPayload?.payload?.sopSummary)
          ? selectedVersionPayload.payload.sopSummary
          : Array.isArray(selectedVersionPayload?.payload?.reviewSnapshot?.sopSummary)
            ? selectedVersionPayload.payload.reviewSnapshot.sopSummary
            : [];

    return raw.map((entry: any) => {
      const metricKey = normaliseMetricKey(entry?.metric);
      const cleanEntry = { ...entry };

      // 1. Cash Flow Adjustment
      const cashFlowQuarterValue = cleanEntry?.cashFlowQuarterAdjustment?.quarterValue;
      if (CASH_FLOW_METRIC_KEYS.has(metricKey) && String(cashFlowQuarterValue || "").trim()) {
        cleanEntry.value = cashFlowQuarterValue;
      } else {
        cleanEntry.cashFlowQuarterAdjustment = null;
      }

      // 2. Profit or Loss Q4 Adjustment
      const profitLossQuarterValue = cleanEntry?.profitLossQuarterAdjustment?.quarterValue;
      if (applyAdjustment) {
        if (PROFIT_LOSS_Q4_METRIC_KEYS.has(metricKey) && String(profitLossQuarterValue || "").trim()) {
          cleanEntry.value = profitLossQuarterValue;
        }
      }

      const isManual = metricKey === "shareprice" || metricKey === "totalnumberofissuedshares";
      if (!isManual && (cleanEntry.value === null || typeof cleanEntry.value === "undefined" || toTrimmed(cleanEntry.value) === "-" || toTrimmed(cleanEntry.value) === "")) {
        cleanEntry.value = "0";
      }

      return cleanEntry;
    });
  }, [
    editablePayload?.sopSummary,
    editablePayload?.reviewSnapshot?.sopSummary,
    selectedVersionPayload?.payload?.sopSummary,
    selectedVersionPayload?.payload?.reviewSnapshot?.sopSummary,
    applyAdjustment,
  ]);
  const reviewSnapshot = editablePayload?.reviewSnapshot || selectedVersionPayload?.payload?.reviewSnapshot;
  const statements = Array.isArray(reviewSnapshot?.statements) ? reviewSnapshot.statements : [];
  const statementImageGroups = useMemo(() => {
    const snapshotImages =
      reviewSnapshot?.statementUploadImagesByStatement &&
      typeof reviewSnapshot.statementUploadImagesByStatement === "object"
        ? reviewSnapshot.statementUploadImagesByStatement
        : null;
    const metadataImages =
      reviewSnapshot?.metadata?.statementUploadImagesByStatement &&
      typeof reviewSnapshot.metadata.statementUploadImagesByStatement === "object"
        ? reviewSnapshot.metadata.statementUploadImagesByStatement
        : null;
    const payloadImages =
      selectedVersionPayload?.payload?.statementUploadImagesByStatement &&
      typeof selectedVersionPayload.payload.statementUploadImagesByStatement === "object"
        ? selectedVersionPayload.payload.statementUploadImagesByStatement
        : null;

    const sourceImages = snapshotImages || metadataImages || payloadImages || {};
    const grouped = new Map<string, any[]>();

    Object.entries(sourceImages).forEach(([rawStatement, images]) => {
      const statementName = normaliseStatementKey(rawStatement);
      if (!statementName || !ALLOWED_QC_STATEMENTS.has(statementName) || !Array.isArray(images)) {
        return;
      }

      const existing = grouped.get(statementName) || [];
      const nextImages = images
        .map((image: any, index: number) => {
          const dataUrl = getImageDataUrl(image);
          if (!dataUrl) {
            return null;
          }
          const defaultIndex = existing.length + index + 1;
          return {
            id: toTrimmed(image?.id) || `${statementName}-${defaultIndex}`,
            name: toTrimmed(image?.name) || `${statementName}-${defaultIndex}.png`,
            dataUrl,
          };
        })
        .filter(Boolean);

      if (!nextImages.length) {
        return;
      }
      grouped.set(statementName, [...existing, ...nextImages]);
    });

    return Array.from(grouped.entries())
      .map(([statementName, images]) => ({
        statementName,
        statementLabel: getStatementLabel(statementName),
        images,
      }))
      .sort((a, b) => {
        const rankDiff = getStatementSortRank(a.statementName) - getStatementSortRank(b.statementName);
        if (rankDiff !== 0) {
          return rankDiff;
        }
        return a.statementLabel.localeCompare(b.statementLabel);
      });
  }, [reviewSnapshot, selectedVersionPayload?.payload?.statementUploadImagesByStatement]);
  const getSubmittedByLabel = useCallback((row: any) => {
    const rowWorkflowId = Number(row?.workflow_id || row?.id || 0);
    const rowVersionId = Number(
      row?.version_id || row?.current_version_id || 0
    );
    const payloadWorkflowId = Number(selectedVersionPayload?.workflow?.id || 0);
    const payloadVersionId = Number(selectedVersionPayload?.version?.id || 0);
    const shouldUseSelectedPayload =
      rowWorkflowId > 0 &&
      rowWorkflowId === payloadWorkflowId &&
      (!rowVersionId || !payloadVersionId || rowVersionId === payloadVersionId);
    const currentVersion = Array.isArray(row?.versions)
      ? row.versions.find((item: any) => Number(item?.id || 0) === Number(row?.current_version_id || 0)) ||
        row.versions.find((item: any) => Number(item?.is_current || 0) === 1) ||
        row.versions[0]
      : null;
    const payloadVersion = shouldUseSelectedPayload ? selectedVersionPayload?.version || null : null;
    const payloadWorkflow = shouldUseSelectedPayload ? selectedVersionPayload?.workflow || null : null;
    const fullName = String(
      row?.submitted_by_full_name ||
        payloadVersion?.submitted_by_full_name ||
        payloadWorkflow?.submitted_by_full_name ||
        currentVersion?.submitted_by_full_name ||
        payloadVersion?.created_by_full_name ||
        currentVersion?.created_by_full_name ||
        payloadWorkflow?.created_by_full_name ||
        row?.created_by_full_name ||
        ""
    ).trim();
    if (fullName) {
      return fullName;
    }
    const username = String(
      row?.submitted_by_username ||
        payloadVersion?.submitted_by_username ||
        payloadWorkflow?.submitted_by_username ||
        currentVersion?.submitted_by_username ||
        payloadVersion?.created_by_username ||
        currentVersion?.created_by_username ||
        payloadWorkflow?.created_by_username ||
        row?.created_by_username ||
        ""
    ).trim();
    if (username) {
      return username;
    }
    const email = String(
      row?.submitted_by_email ||
        payloadVersion?.submitted_by_email ||
        payloadWorkflow?.submitted_by_email ||
        currentVersion?.submitted_by_email ||
        payloadVersion?.created_by_email ||
        currentVersion?.created_by_email ||
        payloadWorkflow?.created_by_email ||
        row?.created_by_email ||
        ""
    ).trim();
    if (email) {
      return email;
    }
    const userId = Number(
      payloadVersion?.created_by ||
      currentVersion?.created_by ||
      payloadWorkflow?.created_by ||
      row?.created_by ||
      0
    );
    return userId > 0 ? `User #${userId}` : "-";
  }, [selectedVersionPayload]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (tickerFilter.trim()) {
        const rowTicker = String(row?.ticker_symbol || "").trim().toLowerCase();
        const searchTicker = tickerFilter.trim().toLowerCase();
        if (!rowTicker.includes(searchTicker)) {
          return false;
        }
      }
      if (submitterFilter.trim()) {
        const rowSubmitter = String(getSubmittedByLabel(row as any)).trim().toLowerCase();
        const searchSubmitter = submitterFilter.trim().toLowerCase();
        if (!rowSubmitter.includes(searchSubmitter)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, tickerFilter, submitterFilter, getSubmittedByLabel]);

  const reviewStatementNames = useMemo(
    () => statements
      .map((statement: any) => normaliseStatementKey(statement?.statement))
      .filter((name: string) => ALLOWED_QC_STATEMENTS.has(name)),
    [statements]
  );
  const activeStatementRecord = useMemo(
    () => statements.find((statement: any) => (
      normaliseStatementKey(statement?.statement) === activeReviewStatement
    )) || null,
    [activeReviewStatement, statements]
  );
  const activeStatementColumns = useMemo(
    () => Array.isArray(activeStatementRecord?.columns) ? activeStatementRecord.columns : [],
    [activeStatementRecord]
  );

  const activeStatementImages = useMemo(() => {
    if (!activeReviewStatement) return [];
    const group = statementImageGroups.find(
      (g) => g.statementName === activeReviewStatement
    );
    return group ? group.images : [];
  }, [activeReviewStatement, statementImageGroups]);

  const categorizedSopSummary = useMemo(() => {
    const categories = {
      income: [] as any[],
      balance: [] as any[],
      cashflow: [] as any[],
      others: [] as any[],
    };

    const mapped = sopSummary.map((item: any, index: number) => ({
      ...item,
      originalIndex: index,
    }));

    const filtered = showOnlyRequiredMetrics
      ? mapped.filter((item: any) => REQUIRED_SOP_METRIC_KEYS.has(normaliseMetricKey(item?.metric)))
      : mapped;

    filtered.forEach((itemWithIndex: any) => {
      const metricName = normaliseMetricKey(itemWithIndex?.metric);

      if (
        metricName.includes("revenue") ||
        metricName.includes("profit") ||
        metricName.includes("loss") ||
        metricName.includes("margin") ||
        metricName.includes("ebit") ||
        metricName.includes("income") ||
        metricName.includes("tax") ||
        metricName.includes("eps") ||
        metricName.includes("dividend") ||
        metricName.includes("interestexpense") ||
        metricName.includes("interestincome") ||
        metricName.includes("interest")
      ) {
        categories.income.push(itemWithIndex);
      } else if (
        metricName.includes("cash") ||
        metricName.includes("cfo") ||
        metricName.includes("cfi") ||
        metricName.includes("cff") ||
        metricName.includes("capex") ||
        metricName.includes("expenditure") ||
        metricName.includes("depreciation") ||
        metricName.includes("amortization") ||
        metricName.includes("amortisation") ||
        metricName.includes("netborrowings")
      ) {
        categories.cashflow.push(itemWithIndex);
      } else if (
        metricName.includes("asset") ||
        metricName.includes("liabilit") ||
        metricName.includes("debt") ||
        metricName.includes("equity") ||
        metricName.includes("borrowing") ||
        metricName.includes("capital") ||
        metricName.includes("reserve") ||
        metricName.includes("goodwill") ||
        metricName.includes("inventor") ||
        metricName.includes("receivable") ||
        metricName.includes("payable") ||
        metricName.includes("bookvalue")
      ) {
        categories.balance.push(itemWithIndex);
      } else {
        categories.others.push(itemWithIndex);
      }
    });

    return categories;
  }, [sopSummary, showOnlyRequiredMetrics]);

  return (
    <div className={`falcon-qc-page${reviewEditorFullscreen ? " review-fullscreen" : ""}`}>
      <div className="falcon-qc-grid">
        <div className="falcon-qc-card">
          <div className="falcon-qc-header">
            <h2>Falcon Senior QC Queue</h2>
            <button type="button" onClick={loadQueue} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="falcon-qc-tabs">
            <button
              type="button"
              className={queueTab === "PENDING" ? "active" : ""}
              onClick={() => setQueueTab("PENDING")}
            >
              Pending
            </button>
            <button
              type="button"
              className={queueTab === "APPROVED" ? "active" : ""}
              onClick={() => setQueueTab("APPROVED")}
            >
              Approved
            </button>
            <button
              type="button"
              className={queueTab === "REJECTED" ? "active" : ""}
              onClick={() => setQueueTab("REJECTED")}
            >
              Rejected
            </button>
          </div>
          <p>Review submissions one-by-one. Approve to commit into production DB.</p>
          <div className="falcon-qc-filter-bar">
            <div className="filter-group">
              <label htmlFor="filter-ticker">Filter Ticker</label>
              <input
                id="filter-ticker"
                type="text"
                value={tickerFilter}
                onChange={(e) => setTickerFilter(e.target.value)}
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="filter-submitter">Filter Submitter</label>
              <input
                id="filter-submitter"
                type="text"
                value={submitterFilter}
                onChange={(e) => setSubmitterFilter(e.target.value)}
                className="filter-input"
              />
            </div>
            {(tickerFilter || submitterFilter) && (
              <button
                type="button"
                className="clear-filters-btn"
                onClick={() => {
                  setTickerFilter("");
                  setSubmitterFilter("");
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div className="falcon-qc-table-wrap">
            <table className="falcon-qc-table">
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Ticker</th>
                  <th>FY</th>
                  <th>Quarter</th>
                  <th>Version</th>
                  <th>Submitted By</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {!rows.length ? (
                  <tr>
                    <td colSpan={7}>No submissions in QC queue.</td>
                  </tr>
                ) : !filteredRows.length ? (
                  <tr>
                    <td colSpan={7}>No submissions match the filters.</td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const rowKey = `${row.workflow_id}-${row.version_id}`;
                    const selectedKey = `${selected?.workflow_id || ""}-${selected?.version_id || ""}`;
                    const isSelected = rowKey === selectedKey;
                    return (
                      <tr
                        key={rowKey}
                        className={isSelected ? "selected" : ""}
                        onClick={() => handleSelect(row)}
                      >
                        <td>{row.workflow_id || row.id}</td>
                        <td>{row.ticker_symbol || "-"}</td>
                        <td>{row.fiscal_year || "-"}</td>
                        <td>{row.quarter_label || row.quarter_date || "-"}</td>
                        <td>{row.version_no || row.latest_version_no || "-"}</td>
                        <td>{getSubmittedByLabel(row as any)}</td>
                        <td>{row.submitted_at || row.last_submitted_at || "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`falcon-qc-card falcon-qc-review-card${reviewEditorFullscreen ? " fullscreen" : ""}`}>
          <div className="falcon-qc-header falcon-qc-review-card-header">
            <h3>Submission Review</h3>
            <button
              type="button"
              onClick={() => setReviewEditorFullscreen((current) => !current)}
            >
              {reviewEditorFullscreen ? "Minimize" : "Maximize"}
            </button>
          </div>
          {!selected ? (
            <p>Select a queue item to review.</p>
          ) : selectedVersionLoading ? (
            <>
              <div className="falcon-qc-meta">
                <div><strong>Workflow:</strong> {selected.workflow_id || selected.id}</div>
                <div><strong>Ticker:</strong> {selected.ticker_symbol}</div>
                <div><strong>Quarter:</strong> {selected.quarter_label || selected.quarter_date}</div>
                <div><strong>Version:</strong> {selected.version_no || selected.latest_version_no}</div>
              </div>
              <div className="falcon-qc-list-empty">Loading workflow details...</div>
            </>
          ) : (
            <>
              <div className="falcon-qc-meta">
                <div><strong>Workflow:</strong> {selected.workflow_id || selected.id}</div>
                <div><strong>Ticker:</strong> {selected.ticker_symbol}</div>
                <div><strong>Quarter:</strong> {selected.quarter_label || selected.quarter_date}</div>
                <div><strong>Version:</strong> {selected.version_no || selected.latest_version_no}</div>
                <div><strong>Submitted by:</strong> {getSubmittedByLabel(selected)}</div>
              </div>

              {/* Workspace Navigation Tabs */}
              <div className="falcon-qc-workspace-tabs">
                <button
                  type="button"
                  className={`workspace-tab-btn ${reviewTab === "sop" ? "active" : ""}`}
                  onClick={() => setReviewTab("sop")}
                >
                  📋 SOP Summary Dashboard
                </button>
                <button
                  type="button"
                  className={`workspace-tab-btn ${reviewTab === "editor" ? "active" : ""}`}
                  onClick={() => setReviewTab("editor")}
                >
                  📊 Statement Cell Editor
                </button>
              </div>

              {/* Tab Content Panes */}
              <div className="falcon-qc-workspace-content">
                {reviewTab === "editor" && (
                  <div className="falcon-qc-tab-pane pane-editor">
                    <div className="falcon-qc-editor-toolbar">
                      {reviewStatementNames.length ? (
                        <div className="falcon-qc-statement-tabs">
                          {reviewStatementNames.map((statementName: string) => (
                            <button
                              key={statementName}
                              type="button"
                              className={activeReviewStatement === statementName ? "active" : ""}
                              onClick={() => setActiveReviewStatement(statementName)}
                            >
                              {getStatementLabel(statementName)}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="falcon-qc-toggle-sidebar-btn"
                        onClick={() => setSideImageVisible((v) => !v)}
                      >
                        {sideImageVisible ? "👁️ Hide Crop Pane" : "👁️ Show Crop Pane"}
                      </button>
                    </div>

                    <div className={`falcon-qc-editor-workspace-split ${sideImageVisible ? "with-sidebar" : ""}`}>
                      <div className="falcon-qc-editor-main-pane">
                        {activeStatementRecord ? (
                          <div className="falcon-qc-editor-table-wrap">
                            <table className="falcon-qc-editor-table">
                              <thead>
                                <tr>
                                  <th>Line Item</th>
                                  {activeStatementColumns.map((columnName: string) => (
                                    <th key={`${activeReviewStatement}-${columnName}`}>{columnName}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(Array.isArray(activeStatementRecord?.lineItems) ? activeStatementRecord.lineItems : []).map((lineItem: any, lineIndex: number) => (
                                  <tr key={`${activeReviewStatement}-${lineIndex}`}>
                                    <td className="line-item-label">{lineItem?.lineItem || "-"}</td>
                                    {activeStatementColumns.map((columnName: string) => (
                                      <td key={`${activeReviewStatement}-${lineIndex}-${columnName}`} className="spreadsheet-cell-td">
                                        <input
                                          type="text"
                                          className="spreadsheet-cell-input"
                                          value={lineItem?.values?.[columnName] ?? ""}
                                          onChange={(event) => handleQcStatementValueChange(
                                            activeReviewStatement,
                                            lineIndex,
                                            columnName,
                                            event.target.value,
                                          )}
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="falcon-qc-list-empty">No statement rows available.</div>
                        )}
                      </div>

                      {sideImageVisible && (
                        <div className="falcon-qc-side-image-pane">
                          <div className="sidebar-image-header">
                            <h5>Reference Crop</h5>
                            <div className="falcon-qc-zoom-controls">
                              <button
                                type="button"
                                onClick={() => setImageZoom((z) => Math.max(1, Number((z - 0.15).toFixed(2))))}
                              >
                                -
                              </button>
                              <span>{Math.round(imageZoom * 100)}%</span>
                              <button
                                type="button"
                                onClick={() => setImageZoom((z) => Math.min(3, Number((z + 0.15).toFixed(2))))}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                className="reset-zoom-btn"
                                onClick={() => setImageZoom(1.35)}
                                title="Reset Zoom"
                              >
                                🔄
                              </button>
                            </div>
                          </div>
                          <div className="sidebar-image-body">
                            {activeStatementImages.length ? (
                              activeStatementImages.map((image: any, index: number) => (
                                <figure className="sidebar-image-card" key={`sidebar-${activeReviewStatement}-${image?.id || index}`}>
                                  <div className="sidebar-image-wrap">
                                    <img
                                      src={image?.dataUrl}
                                      alt={`${activeReviewStatement} Crop ${index + 1}`}
                                      style={{ transform: `scale(${imageZoom})` }}
                                      loading="lazy"
                                    />
                                  </div>
                                  <figcaption>{image?.name || `Crop ${index + 1}`}</figcaption>
                                </figure>
                              ))
                            ) : (
                              <div className="falcon-qc-list-empty">
                                No cropped images linked to this statement.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {reviewTab === "sop" && (
                  <div className="falcon-qc-tab-pane pane-sop">
                    <div className="sop-dashboard-toolbar">
                      <div className="toolbar-left">
                        <span className="toolbar-info">💡 Showing classified metrics. Toggle off to view all line items.</span>
                      </div>
                      <div className="toolbar-right">
                        <label className="required-metrics-toggle-label">
                          <input
                            type="checkbox"
                            className="required-metrics-checkbox"
                            checked={showOnlyRequiredMetrics}
                            onChange={(e) => setShowOnlyRequiredMetrics(e.target.checked)}
                          />
                          <span className="toggle-switch-slider"></span>
                          <span className="toggle-text">Show Only Required Metrics</span>
                        </label>
                      </div>
                    </div>
                    <div className="currency-conversion-panel">
                      <label className="currency-toggle-label">
                        <input
                          type="checkbox"
                          checked={conversionEnabled}
                          onChange={(e) => updateCurrencyConversionConfig(e.target.checked, baseCurrency, targetCurrency)}
                        />
                        <span>Enable Currency Conversion</span>
                      </label>
                      {conversionEnabled && (
                        <div className="currency-select-group">
                          <span>Source:</span>
                          <select
                            value={baseCurrency}
                            onChange={(e) => updateCurrencyConversionConfig(conversionEnabled, e.target.value, targetCurrency)}
                          >
                            {["USD", "LKR", "GBP", "EUR", "INR", "AED", "SAR", "QAR", "BHD", "KWD", "SGD", "SDG", "CAD", "AUD"].map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <span>Target:</span>
                          <select
                            value={targetCurrency}
                            onChange={(e) => updateCurrencyConversionConfig(conversionEnabled, baseCurrency, e.target.value)}
                          >
                            {["USD", "LKR", "GBP", "EUR", "INR", "AED", "SAR", "QAR", "BHD", "KWD", "SGD", "SDG", "CAD", "AUD"].map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <span className="exchange-rate-badge">
                            {exchangeRateLoading ? (
                              "Fetching rate..."
                            ) : Number.isFinite(Number(exchangeRate)) && Number(exchangeRate) > 0 ? (
                              `Actual Rate (${exchangeRateDate || "Selected Quarter"}): 1 ${baseCurrency} = ${exchangeRate} ${targetCurrency}`
                            ) : (
                              `Rate unavailable (${exchangeRateDate || "Selected Quarter"}): ${baseCurrency} to ${targetCurrency}`
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    {(() => {
                      const cashFlowSopRows = sopSummary.filter((item: any) =>
                        CASH_FLOW_METRIC_KEYS.has(normaliseMetricKey(item?.metric))
                      );
                      const cashFlowAdjustedRows = cashFlowSopRows.filter((item: any) =>
                        Boolean(item?.cashFlowQuarterAdjustment)
                      );
                      if (!cashFlowAdjustedRows.length) return null;
                      return (
                        <div className="sop-cashflow-section">
                          <div className="sop-cashflow-header">
                            <h4>Cash Flow Quarter Adjustment (Database Based)</h4>
                            <p>
                              For cumulative cash flow values, Falcon scales extracted cumulative and subtracts the same fiscal year's previous quarter totals from DB.
                            </p>
                          </div>
                          <div className="sop-cashflow-table-wrapper">
                            <table className="sop-cashflow-table">
                              <thead>
                                <tr>
                                  <th>Metric</th>
                                  <th>Selected Quarter</th>
                                  <th>Extracted Cumulative</th>
                                  <th>Previous FY Quarters (DB)</th>
                                  <th>This Quarter (Saved/Shown)</th>
                                  {conversionEnabled && exchangeRate && baseCurrency !== targetCurrency && (
                                    <th>Final Converted ({targetCurrency})</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {cashFlowAdjustedRows.map((row: any) => {
                                  const qNum = parseNumericValue(row.cashFlowQuarterAdjustment?.quarterValue);
                                  const convertedVal =
                                    qNum !== null && exchangeRate
                                      ? `${targetCurrency} ${(qNum * exchangeRate).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                        })}`
                                      : "-";
                                  return (
                                    <tr key={`cashflow-${row.metric}`}>
                                      <td>{row.metric || "-"}</td>
                                      <td>{row.cashFlowQuarterAdjustment?.selectedQuarter || "-"}</td>
                                      <td>{row.cashFlowQuarterAdjustment?.extractedCumulative || "-"}</td>
                                      <td>{row.cashFlowQuarterAdjustment?.previousCumulative || "-"}</td>
                                      <td>{row.cashFlowQuarterAdjustment?.quarterValue || "-"}</td>
                                      {conversionEnabled && exchangeRate && baseCurrency !== targetCurrency && (
                                        <td style={{ color: "#166534", fontWeight: 700 }}>
                                          {convertedVal}
                                          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 600 }}>
                                            1 {baseCurrency} = {formatCurrencyPreviewValue(Number(exchangeRate), 6)} {targetCurrency}
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="sop-dashboard-grid">
                      {/* Income Card */}
                      <div className="sop-category-card card-income">
                        <div className="sop-category-header">
                          <span className="sop-category-icon">📊</span>
                          <h4>Profit & Loss Metrics</h4>
                        </div>
                        <div className="sop-category-body">
                          {categorizedSopSummary.income.length ? (
                            categorizedSopSummary.income.map((item) => {
                              const cashFlowQuarterAdjustment = item.cashFlowQuarterAdjustment;
                              const profitLossQuarterAdjustment = item.profitLossQuarterAdjustment;
                              return (
                                <div key={`${item.metric}-${item.originalIndex}`} className="sop-category-item-container">
                                  <div className="sop-category-row">
                                    <span className="sop-metric-label" title={item.metric}>{item.metric}</span>
                                    {renderSopValueControl(item)}
                                  </div>
                                  {cashFlowQuarterAdjustment && (
                                    <div className="sop-cashflow-quarter-detail">
                                      {cashFlowQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {cashFlowQuarterAdjustment.selectedQuarter}
                                        </span>
                                      )}
                                      <span>
                                        Cum: {cashFlowQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        Prev: {cashFlowQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span>
                                        This Q: {cashFlowQuarterAdjustment.quarterValue}
                                      </span>
                                    </div>
                                  )}
                                  {profitLossQuarterAdjustment && applyAdjustment && (
                                    <div className={`sop-cashflow-quarter-detail profit-loss${!applyAdjustment ? ' sop-pl-detail-disabled' : ' sop-pl-detail-active'}`}>
                                      {profitLossQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {profitLossQuarterAdjustment.selectedQuarter}
                                          {!applyAdjustment ? ' · Toggle OFF' : ' · Toggle ON'}
                                        </span>
                                      )}
                                      <span>
                                        <strong>Extracted:</strong> {profitLossQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        <strong>Prev Q:</strong> {profitLossQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span className={!applyAdjustment ? '' : 'sop-pl-this-quarter-badge'}>
                                        <strong>This Q:</strong> {profitLossQuarterAdjustment.quarterValue}
                                        {!applyAdjustment
                                          ? <span className="sop-pl-preview-label"> (preview, not applied)</span>
                                          : <span className="sop-pl-applied-label"> ✓ sent to QC</span>}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="sop-empty-state">No income statement metrics found.</div>
                          )}
                        </div>
                      </div>

                      {/* Balance Sheet Card */}
                      <div className="sop-category-card card-balance">
                        <div className="sop-category-header">
                          <span className="sop-category-icon">📋</span>
                          <h4>Balance Sheet Metrics</h4>
                        </div>
                        <div className="sop-category-body">
                          {categorizedSopSummary.balance.length ? (
                            categorizedSopSummary.balance.map((item) => {
                              const cashFlowQuarterAdjustment = item.cashFlowQuarterAdjustment;
                              const profitLossQuarterAdjustment = item.profitLossQuarterAdjustment;
                              return (
                                <div key={`${item.metric}-${item.originalIndex}`} className="sop-category-item-container">
                                  <div className="sop-category-row">
                                    <span className="sop-metric-label" title={item.metric}>{item.metric}</span>
                                    {renderSopValueControl(item)}
                                  </div>
                                  {cashFlowQuarterAdjustment && (
                                    <div className="sop-cashflow-quarter-detail">
                                      {cashFlowQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {cashFlowQuarterAdjustment.selectedQuarter}
                                        </span>
                                      )}
                                      <span>
                                        Cum: {cashFlowQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        Prev: {cashFlowQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span>
                                        This Q: {cashFlowQuarterAdjustment.quarterValue}
                                      </span>
                                    </div>
                                  )}
                                  {profitLossQuarterAdjustment && applyAdjustment && (
                                    <div className={`sop-cashflow-quarter-detail profit-loss${!applyAdjustment ? ' sop-pl-detail-disabled' : ' sop-pl-detail-active'}`}>
                                      {profitLossQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {profitLossQuarterAdjustment.selectedQuarter}
                                          {!applyAdjustment ? ' · Toggle OFF' : ' · Toggle ON'}
                                        </span>
                                      )}
                                      <span>
                                        <strong>Extracted:</strong> {profitLossQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        <strong>Prev Q:</strong> {profitLossQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span className={!applyAdjustment ? '' : 'sop-pl-this-quarter-badge'}>
                                        <strong>This Q:</strong> {profitLossQuarterAdjustment.quarterValue}
                                        {!applyAdjustment
                                          ? <span className="sop-pl-preview-label"> (preview, not applied)</span>
                                          : <span className="sop-pl-applied-label"> ✓ sent to QC</span>}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="sop-empty-state">No balance sheet metrics found.</div>
                          )}
                        </div>
                      </div>

                      {/* Cash Flow Card */}
                      <div className="sop-category-card card-cashflow">
                        <div className="sop-category-header">
                          <span className="sop-category-icon">💸</span>
                          <h4>Cash Flow Metrics</h4>
                        </div>
                        <div className="sop-category-body">
                          {categorizedSopSummary.cashflow.length ? (
                            categorizedSopSummary.cashflow.map((item) => {
                              const cashFlowQuarterAdjustment = item.cashFlowQuarterAdjustment;
                              const profitLossQuarterAdjustment = item.profitLossQuarterAdjustment;
                              return (
                                <div key={`${item.metric}-${item.originalIndex}`} className="sop-category-item-container">
                                  <div className="sop-category-row">
                                    <span className="sop-metric-label" title={item.metric}>{item.metric}</span>
                                    {renderSopValueControl(item)}
                                  </div>
                                  {cashFlowQuarterAdjustment && (
                                    <div className="sop-cashflow-quarter-detail">
                                      {cashFlowQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {cashFlowQuarterAdjustment.selectedQuarter}
                                        </span>
                                      )}
                                      <span>
                                        Cum: {cashFlowQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        Prev: {cashFlowQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span>
                                        This Q: {cashFlowQuarterAdjustment.quarterValue}
                                      </span>
                                    </div>
                                  )}
                                  {profitLossQuarterAdjustment && applyAdjustment && (
                                    <div className={`sop-cashflow-quarter-detail profit-loss${!applyAdjustment ? ' sop-pl-detail-disabled' : ' sop-pl-detail-active'}`}>
                                      {profitLossQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {profitLossQuarterAdjustment.selectedQuarter}
                                          {!applyAdjustment ? ' · Toggle OFF' : ' · Toggle ON'}
                                        </span>
                                      )}
                                      <span>
                                        <strong>Extracted:</strong> {profitLossQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        <strong>Prev Q:</strong> {profitLossQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span className={!applyAdjustment ? '' : 'sop-pl-this-quarter-badge'}>
                                        <strong>This Q:</strong> {profitLossQuarterAdjustment.quarterValue}
                                        {!applyAdjustment
                                          ? <span className="sop-pl-preview-label"> (preview, not applied)</span>
                                          : <span className="sop-pl-applied-label"> ✓ sent to QC</span>}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="sop-empty-state">No cash flow metrics found.</div>
                          )}
                        </div>
                      </div>

                      {/* Others Card */}
                      <div className="sop-category-card card-others">
                        <div className="sop-category-header">
                          <span className="sop-category-icon">⚙️</span>
                          <h4>Miscellaneous Metrics</h4>
                        </div>
                        <div className="sop-category-body">
                          {categorizedSopSummary.others.length ? (
                            categorizedSopSummary.others.map((item) => {
                              const cashFlowQuarterAdjustment = item.cashFlowQuarterAdjustment;
                              const profitLossQuarterAdjustment = item.profitLossQuarterAdjustment;
                              return (
                                <div key={`${item.metric}-${item.originalIndex}`} className="sop-category-item-container">
                                  <div className="sop-category-row">
                                    <span className="sop-metric-label" title={item.metric}>{item.metric}</span>
                                    {renderSopValueControl(item)}
                                  </div>
                                  {cashFlowQuarterAdjustment && (
                                    <div className="sop-cashflow-quarter-detail">
                                      {cashFlowQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {cashFlowQuarterAdjustment.selectedQuarter}
                                        </span>
                                      )}
                                      <span>
                                        Cum: {cashFlowQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        Prev: {cashFlowQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span>
                                        This Q: {cashFlowQuarterAdjustment.quarterValue}
                                      </span>
                                    </div>
                                  )}
                                  {profitLossQuarterAdjustment && applyAdjustment && (
                                    <div className={`sop-cashflow-quarter-detail profit-loss${!applyAdjustment ? ' sop-pl-detail-disabled' : ' sop-pl-detail-active'}`}>
                                      {profitLossQuarterAdjustment.selectedQuarter && (
                                        <span className="sop-cashflow-quarter-label">
                                          {profitLossQuarterAdjustment.selectedQuarter}
                                          {!applyAdjustment ? ' · Toggle OFF' : ' · Toggle ON'}
                                        </span>
                                      )}
                                      <span>
                                        <strong>Extracted:</strong> {profitLossQuarterAdjustment.extractedCumulative}
                                      </span>
                                      <span>
                                        <strong>Prev Q:</strong> {profitLossQuarterAdjustment.previousCumulative}
                                      </span>
                                      <span className={!applyAdjustment ? '' : 'sop-pl-this-quarter-badge'}>
                                        <strong>This Q:</strong> {profitLossQuarterAdjustment.quarterValue}
                                        {!applyAdjustment
                                          ? <span className="sop-pl-preview-label"> (preview, not applied)</span>
                                          : <span className="sop-pl-applied-label"> ✓ sent to QC</span>}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="sop-empty-state">No miscellaneous metrics found.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* PERSISTENT ACTIONS FOOTER */}
              <div className="falcon-qc-review-footer">
                <div className="comment-block">
                  <label htmlFor="qc-comment-textarea">QC Comment (optional)</label>
                  <textarea
                    id="qc-comment-textarea"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    rows={3}
                    placeholder="Add approval/rejection note..."
                  />
                </div>

                {queueTab === "PENDING" && (
                  <div className="falcon-qc-actions">
                    <button
                      type="button"
                      className="approve-btn"
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Processing..." : "✓ Approve & Commit"}
                    </button>
                    <button
                      type="button"
                      className="reject-btn"
                      onClick={handleReject}
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Processing..." : "✗ Reject Submission"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {message ? <div className="falcon-qc-message">{message}</div> : null}
        </div>
      </div>
    </div>
  );
};

export default AnnualReportQcQueue;
